import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * The checkmark burst, injected into a size button the first time it is
 * picked. It used to be inlined by Liquid into every button (see
 * snippets/it-card-bag-size.liquid); on a 24-card grid that was 284 copies and
 * 43 percent of the page's DOM nodes. Same markup as assets/icon-checkmark-burst.svg.
 */
const BURST_SVG = '<svg aria-hidden="true" class="checkmark-burst" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g class="check"><circle class="ring" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path class="tick" d="M9 12.75L11.25 15L15 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></g><g class="burst"><g style="--index: 0;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 1;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 2;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 3;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 4;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 5;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 6;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g><g style="--index: 7;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g></g></svg>';

/**
 * IT card bag — opens the size strip over the foot of the card image.
 *
 * Only the strip's open/close is handled here. Every size chip is an
 * <add-to-cart-component> inside a <product-form-component>, so pressing
 * one submits Horizon's product form: product-form.js posts the line,
 * announces the standard cart events, opens the drawer, and writes any
 * error into the strip. This element sets the variant the form submits,
 * guards against a second press while an add is in flight, and closes the
 * strip once the cart has answered.
 *
 * The strip is cloned from a <template> and mounted INSIDE this element,
 * so the chips resolve `on:click="it-card-bag/pick"` through `closest()`
 * the way every other Horizon handler does, and refs stay in scope. It is
 * positioned over the gallery by CSS alone (see blocks/it-card-bag.liquid).
 *
 * Being a custom element, it re-initialises itself whenever <results-list>
 * replaces the grid - connectedCallback runs on insertion - so nothing
 * needs to be delegated from `document`.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} bag - The bag icon button.
 * @property {HTMLTemplateElement} sizes - The strip markup.
 * @extends Component<Refs>
 */
class ItCardBag extends Component {
  requiredRefs = ['bag', 'sizes'];

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {boolean} */
  #busy = false;

  /** @type {AbortController | null} */
  #panelListeners = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.close();
  }

  /**
   * The bag icon toggles the strip. Wired from the button's `on:click`.
   * @param {MouseEvent} event
   */
  toggle(event) {
    // The card's overlay link and the title link both sit around the icon;
    // an unhandled press would navigate to the PDP before the strip opened.
    event.preventDefault();
    event.stopPropagation();

    if (this.#panel) {
      this.close();
      return;
    }

    this.open();
  }

  open() {
    if (openInstance && openInstance !== this) openInstance.close();

    const fragment = /** @type {DocumentFragment} */ (this.refs.sizes.content.cloneNode(true));
    const panel = fragment.querySelector('[data-it-sizepop]');
    if (!(panel instanceof HTMLElement)) return;

    this.append(fragment);
    this.#panel = panel;
    this.#busy = false;
    this.refs.bag.setAttribute('aria-expanded', 'true');

    const controller = new AbortController();
    const { signal } = controller;
    this.#panelListeners = controller;

    // product-form.js announces the add on the form component itself; the
    // event bubbles up through the strip to here.
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });

    openInstance = this;
    // The entrance is CSS (@starting-style on .it-sizepop); nothing to schedule.

    this.#syncWithCart(panel);
  }

  /**
   * Retires any chip whose variant already sits at its stock ceiling in the
   * cart, so the customer is not offered a size Shopify would refuse. The
   * cart comes from Horizon's own cart-items component when it is on the
   * page (it dedupes in-flight fetches), else from the cart endpoint.
   * @param {HTMLElement} panel
   */
  async #syncWithCart(panel) {
    const chips = /** @type {HTMLButtonElement[]} */ ([...panel.querySelectorAll('button[data-variant][data-max]')]);
    if (chips.length === 0) return;

    let cart;
    try {
      /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
      const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
      cart = cartItems?.fetchCartData
        ? await cartItems.fetchCartData()
        : await fetch(`${Theme.routes.cart_url}.json`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then((response) => response.json());
    } catch {
      return;
    }

    // The strip may have closed, or been reopened for another card, meanwhile.
    if (this.#panel !== panel) return;

    for (const chip of chips) {
      const inCart = cart.items
        .filter((item) => String(item.variant_id) === chip.dataset.variant)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (inCart >= Number(chip.dataset.max)) {
        // Not `disabled`: a dead button reads as sold out, and a press on it
        // could not explain itself. It stays pressable and answers instead.
        chip.dataset.inCart = 'true';
        chip.setAttribute('aria-disabled', 'true');
        chip.title = this.dataset.maxedLabel ?? '';
      }
    }
  }

  close() {
    if (openInstance === this) openInstance = null;

    this.#panelListeners?.abort();
    this.#panelListeners = null;
    this.#busy = false;

    this.#panel?.remove();
    this.#panel = null;
    this.refs.bag?.setAttribute('aria-expanded', 'false');
  }

  /**
   * A press on a size chip, wired from the chip's `on:click="it-card-bag/pick"`.
   *
   * Sets the variant the form will submit, hands the click to the chip's own
   * <add-to-cart-component> for the checkmark burst and fly-to-cart, then lets
   * the button's native submit carry on into product-form.js.
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  pick(event) {
    const chip = event.target.closest('button[data-variant]');
    const input = this.#panel?.querySelector('input[name="id"]');

    if (
      this.#busy ||
      !(chip instanceof HTMLButtonElement) ||
      !(input instanceof HTMLInputElement) ||
      !chip.dataset.variant
    ) {
      event.preventDefault();
      return;
    }

    if (chip.dataset.inCart) {
      event.preventDefault();
      this.#say(`${chip.textContent.trim()} ${this.dataset.maxedSentence ?? 'is already in your cart'}`);
      return;
    }

    input.value = chip.dataset.variant;
    this.#busy = true;
    this.#panel?.setAttribute('aria-busy', 'true');

    const icon = chip.querySelector('.add-to-cart__added-icon');
    if (icon && !icon.firstElementChild) icon.innerHTML = BURST_SVG;

    /** @type {import('./product-form.js').AddToCartComponent | null} */
    const addToCart = /** @type {any} */ (chip.closest('add-to-cart-component'));
    addToCart?.handleClick?.(event);
  }

  /**
   * product-form.js settles the carried promise with the cart's answer.
   * Close on success; on failure the strip stays open with the message
   * product-form.js wrote into it.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onLinesUpdate = async (event) => {
    if (event.action !== 'add') return;

    try {
      const result = await event.promise;
      if (result?.detail?.didError) {
        this.#release();
        return;
      }
      this.close();
    } catch {
      this.#release();
    }
  };

  #release() {
    this.#busy = false;
    this.#panel?.removeAttribute('aria-busy');
  }

  /**
   * Writes a line into the strip's message slot — the same span, and the
   * same text node, product-form.js uses for a refused add.
   * @param {string} message
   */
  #say(message) {
    const slot = this.#panel?.querySelector('.it-sizepop__error');
    if (!(slot instanceof HTMLElement)) return;

    slot.classList.remove('hidden');
    const textNode = slot.childNodes[2];
    if (textNode) {
      textNode.textContent = ` ${message}`;
    } else {
      slot.append(` ${message}`);
    }
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.contains(target)) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };
}

/** Only one strip is ever open. @type {ItCardBag | null} */
let openInstance = null;

if (!customElements.get('it-card-bag')) {
  customElements.define('it-card-bag', ItCardBag);
}
